import React from "react";
import { Box, Typography, Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4),
    backgroundColor: "#0f0f0f",
    minHeight: "100vh",
    color: "#fff",
    lineHeight: 1.6,
  },
  title: {
    color: "#FE5A97",
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
  },
  subtitle: {
    color: "#FE5A97",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: "bold",
  },
  paragraph: {
    marginBottom: theme.spacing(2),
    color: "#fff",
  },
  listItem: {
    marginBottom: theme.spacing(1),
    color: "#fff",
    paddingLeft: theme.spacing(2),
  },
  contactInfo: {
    backgroundColor: "#1a1a1a",
    border: "1px solid #333",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  lastUpdated: {
    color: "#ccc",
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
  },
  link: {
    color: "#FE5A97",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

export default function DMCATakedownPolicy() {
  const classes = useStyles();
  const seo = useSEO("DMCA-takedown-policy");

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <Container maxWidth="md" className={classes.container}>
        <Typography variant="h3" className={classes.title}>
          DMCA Takedown Policy
        </Typography>

        <Typography variant="body2" className={classes.lastUpdated}>
          Effective Date: June 30, 2025
        </Typography>

        <Typography variant="body1" className={classes.paragraph}>
          <strong>Platform:</strong>{" "}
          <a href="https://SugarCribs.com" className={classes.link}>SugarCribs.com</a>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          SugarCribs.com respects the intellectual property rights of others
          and expects its users and content creators to do the same. In
          accordance with the Digital Millennium Copyright Act (DMCA), we
          have established the following procedure for the submission and
          handling of copyright infringement claims.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          1. Reporting Copyright Infringement
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you believe that your copyrighted work has been copied and is
          accessible on SugarCribs.com in a way that constitutes copyright
          infringement, you may submit a DMCA takedown notice by providing
          us with a written communication containing the following
          information:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>
            A physical or electronic signature of the copyright owner or a
            person authorized to act on their behalf.
          </li>
          <li className={classes.listItem}>
            Identification of the copyrighted work claimed to have been
            infringed.
          </li>
          <li className={classes.listItem}>
            Identification of the material that is claimed to be infringing,
            including the URL or other specific information to help us
            locate the material.
          </li>
          <li className={classes.listItem}>Your name, address, telephone number, and email address.</li>
          <li className={classes.listItem}>
            A statement that you have a good faith belief that the use of
            the material is not authorized by the copyright owner, its
            agent, or the law.
          </li>
          <li className={classes.listItem}>
            A statement that the information in your notice is accurate, and
            under penalty of perjury, that you are the copyright owner or
            authorized to act on their behalf.
          </li>
        </Box>

        <Typography variant="h5" className={classes.subtitle}>
          2. Submitting a DMCA Notice
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Please send all DMCA takedown notices to:
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Email:</strong>{" "}
          <a href="mailto:dmca@sugarcribs.com" className={classes.link}>dmca@sugarcribs.com</a>
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          <strong>Subject Line:</strong> DMCA Takedown Request
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          3. Counter Notification
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you believe that your content was removed or disabled as a
          result of a mistake or misidentification, you may submit a
          counter-notification to us that includes:
        </Typography>
        <Box component="ul" style={{ paddingLeft: "20px" }}>
          <li className={classes.listItem}>Your physical or electronic signature.</li>
          <li className={classes.listItem}>
            Identification of the material that has been removed or to which
            access has been disabled, including the URL where the material
            appeared before it was removed.
          </li>
          <li className={classes.listItem}>
            A statement under penalty of perjury that you believe the
            material was removed by mistake or misidentification.
          </li>
          <li className={classes.listItem}>Your name, address, and telephone number.</li>
          <li className={classes.listItem}>
            A statement that you consent to the jurisdiction of the federal
            court in your district (or where your service provider is
            located), and that you will accept service of process from the
            person who submitted the original takedown notice.
          </li>
        </Box>
        <Typography variant="body1" className={classes.paragraph}>
          Counter-notifications should be sent to:{" "}
          <a href="mailto:dmca@sugarcribs.com" className={classes.link}>dmca@sugarcribs.com</a>
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          4. Repeat Infringer Policy
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Sugar Cribs will, in appropriate circumstances, disable and/or
          terminate the accounts of users who are repeat infringers. A
          repeat infringer is a user who has been the subject of more than
          one valid DMCA takedown notice.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          5. Good Faith and Abuse Policy
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Submitting false claims under the DMCA may result in legal
          liability. Please ensure your claim is accurate and submitted in
          good faith. Abuse of this policy will result in termination of
          access to SugarCribs.com and potential legal action.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          6. Modifications
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          Sugar Cribs reserves the right to modify this policy at any time.
          Users are encouraged to regularly review this page for the latest
          information on our DMCA compliance procedures.
        </Typography>

        <Typography variant="h5" className={classes.subtitle}>
          7. Contact Us
        </Typography>
        <Typography variant="body1" className={classes.paragraph}>
          If you have any questions about this policy, please contact:
        </Typography>

        <Box className={classes.contactInfo}>
          <Typography
            variant="h6"
            style={{ color: "#FE5A97", marginBottom: "16px" }}
          >
            Contact Information
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>Email:</strong>{" "}
            <a href="mailto:support@sugarcribs.com" className={classes.link}>support@sugarcribs.com</a>
          </Typography>
          <Typography variant="body1" className={classes.paragraph}>
            <strong>Website:</strong>{" "}
            <a href="https://SugarCribs.com" className={classes.link}>https://SugarCribs.com</a>
          </Typography>
        </Box>
      </Container>
    </>
  );
}
